import { hash, toUnitFloat } from "daedalus-core";
import { SoundActor, npcHook, type NpcEvent } from "odyssey-shared";
import type { PlaySound } from "./cues.ts";

/**
 * An NPC's sounds in the world (the amendment for P2.22-T36): an occasional idle call or wing flutter from each NPC
 * whose look names a sound folder, placed where it stands. The timing is a pure function of the NPC's handle and the
 * scene clock, with no random source, so it is the same for a given handle and time on every run. The knobs are
 * here.
 */

/**
 * The length of one window of the scene clock, in ms. Each NPC in hearing range sounds at most once in a window.
 */
export const NPC_AMBIENT_WINDOW_MS = 14_000;

/**
 * How many windows make a block. An NPC sounds in exactly one window of each block, which one a function of its handle
 * and the block, so it is heard about every three windows and never goes longer than five without a sound.
 */
export const NPC_AMBIENT_BLOCK_WINDOWS = 3;

/**
 * The share of the sounds an NPC makes that are its wing flutter rather than its idle call.
 */
export const NPC_FLUTTER_SHARE = 0.35;

/**
 * How far from the listener, across or down in tiles, an NPC is considered for an idle sound; the mixer's vision
 * rectangle decides the rest.
 */
export const NPC_AMBIENT_REACH_TILES = 8;

/**
 * The earliest and latest point of a window a sound lands at, as a share of the window, so a sound never lands as a
 * window turns.
 */
const SOUND_SPAN: readonly [number, number] = [0.1, 0.9];

const SEED_BLOCK = 0x6e70c001;
const SEED_AT = 0x6e70c002;
const SEED_KIND = 0x6e70c003;
const MAX_REMEMBERED = 256;

interface Slot {
  window: number;
  at: number;
  event: NpcEvent | null;
  quietUntil: number;
}

/**
 * How long an NPC stays quiet in the world after one of its conversation cues starts, in ms: longer than the longest
 * master, so an idle call never cuts its own greeting, blip or farewell.
 */
export const NPC_CONVERSATION_QUIET_MS = 3000;

/**
 * Told of every NPC drawn, each frame, for its idle sounds: its handle, its look's appearance, the scene time in ms and
 * its offset from the local character in tiles.
 */
export type NpcListener = (handle: number, appearance: number, time: number, dx: number, dy: number) => void;

/**
 * Whether a dialogue line asks a question, which the NPC sounds its curious call for: its trimmed text ends in a
 * question mark, Latin or fullwidth.
 *
 * @param text The line's text.
 * @returns True for a question.
 * @example
 * isQuestion("Have you seen the owls? ");
 * // true
 */
export function isQuestion(text: string): boolean {
  const trimmed = text.trimEnd();
  return trimmed.endsWith("?") || trimmed.endsWith("？");
}

/**
 * The window of its block an NPC sounds in.
 *
 * @param handle The NPC's handle.
 * @param window The window's number.
 * @returns A number from 0 to the block's length less one.
 */
function chosenWindow(handle: number, window: number): number {
  return Math.floor(toUnitFloat(hash(SEED_BLOCK, handle, Math.floor(window / NPC_AMBIENT_BLOCK_WINDOWS))) * NPC_AMBIENT_BLOCK_WINDOWS);
}

/**
 * The idle sounds of the NPCs in view. Each NPC is decided once a window; the frame in between costs one map read and
 * a comparison and allocates nothing.
 */
export class NpcAmbience {
  private readonly slots = new Map<number, Slot>();
  private now = 0;

  /**
   * @param play Plays a hook.
   */
  public constructor(private readonly play: PlaySound) {}

  /**
   * Tells the ambience an NPC is drawn this frame, and sounds its idle or flutter when its moment in the window has
   * come. An NPC out of reach, or whose look names no sound folder, sounds nothing.
   *
   * @param handle The NPC's handle.
   * @param sound Its look's sound folder, null for none.
   * @param time Scene time in ms.
   * @param dx Its offset across from the listener, in tiles.
   * @param dy Its offset down.
   */
  public near(handle: number, sound: string | null, time: number, dx: number, dy: number): void {
    if (sound === null || Math.abs(dx) > NPC_AMBIENT_REACH_TILES || Math.abs(dy) > NPC_AMBIENT_REACH_TILES || time < 0) {
      return;
    }
    const window = Math.floor(time / NPC_AMBIENT_WINDOW_MS);
    this.now = time;
    const slot = this.slotOf(handle);
    if (slot.window !== window) {
      const start = window * NPC_AMBIENT_WINDOW_MS;
      slot.window = window;
      slot.at = start + (SOUND_SPAN[0] + toUnitFloat(hash(SEED_AT, handle, window)) * (SOUND_SPAN[1] - SOUND_SPAN[0])) * NPC_AMBIENT_WINDOW_MS;
      slot.event = time > slot.at || window % NPC_AMBIENT_BLOCK_WINDOWS !== chosenWindow(handle, window) ? null : toUnitFloat(hash(SEED_KIND, handle, window)) < NPC_FLUTTER_SHARE ? "wing_flutter" : "idle";
    }
    if (slot.event !== null && time >= slot.at) {
      if (time < slot.quietUntil) {
        slot.event = null;
        return;
      }
      this.play(npcHook(sound, slot.event), SoundActor.Npc, dx, dy, handle);
      slot.event = null;
    }
  }

  /**
   * Keeps an NPC quiet in the world for a conversation cue it just started (`NPC_CONVERSATION_QUIET_MS`).
   *
   * @param handle The NPC's handle.
   */
  public speaking(handle: number): void {
    this.slotOf(handle).quietUntil = this.now + NPC_CONVERSATION_QUIET_MS;
  }

  /**
   * The slot of an NPC, made when first asked for.
   *
   * @param handle The NPC's handle.
   * @returns The slot.
   */
  private slotOf(handle: number): Slot {
    let slot = this.slots.get(handle);
    if (slot === undefined) {
      if (this.slots.size >= MAX_REMEMBERED) {
        this.slots.clear();
      }
      slot = { window: -1, at: 0, event: null, quietUntil: 0 };
      this.slots.set(handle, slot);
    }
    return slot;
  }

  /**
   * Forgets every NPC, as a session ends.
   */
  public clear(): void {
    this.slots.clear();
  }
}
